import { LoadingOutlined } from "@ant-design/icons";
import { notification, Spin } from "antd";
import { ZEE_CATEGORY_TYPES } from "constants/phase";
import Papa from "papaparse";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import Modal from "react-modal";

const ShowCSV = ({
  setIsShowCsv,
  isShowCsv,
  streamDetails,
  clipDetails,
  getCsvData: fetchCsvData,
  getByStreamIdOnly,
}) => {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(null);

  // Memoize the CSV URL to avoid recalculation on every render
  const csvUrl = useMemo(() => {
    if (streamDetails) {
      const categoryType = streamDetails?.fields?.categoryType;
      const fileKey =
        categoryType === ZEE_CATEGORY_TYPES.PERFORMANCE
          ? streamDetails?.joinedFileUpdateKey
          : streamDetails?.joinedFileKey;
      return `${streamDetails?.fields?.cdn_suffix}${fileKey}`.replace(".mp4", ".csv");
    }
    if (clipDetails) {
      return clipDetails?.outputSceneCsvKey?.replace(".mp4", ".csv");
    }
    return null;
  }, [streamDetails, clipDetails]);

  // Memoize loader icon
  const antIcon = useMemo(() => <LoadingOutlined style={{ fontSize: 30 }} spin />, []);

  // Memoized close handler
  const handleClose = useCallback(() => {
    setIsShowCsv(false);
    setError(null);
  }, [setIsShowCsv]);

  // Parse CSV data and update state
  const parseCsvData = useCallback((csvData) => {
    if (!csvData) {
      setLoader(false);
      return;
    }

    Papa.parse(csvData, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.data && result.data.length > 0) {
          // Extract columns
          const extractedColumns = Object.keys(result.data[0]).map((columnName) => ({
            name: columnName,
            selector: (row) => row[columnName],
            sortable: true,
          }));

          // Extract data with IDs
          const extractedData = result.data.map((row, index) => ({
            id: index + 1,
            ...row,
          }));

          setColumns(extractedColumns);
          setData(extractedData);
          setError(null);
        } else {
          setError("No data found in CSV file");
        }
        setLoader(false);
      },
      error: (err) => {
        const errorMessage = `CSV parsing error: ${err.message}`;
        notification.error({ message: errorMessage });
        setError(errorMessage);
        setLoader(false);
      },
    });
  }, []);

  // Fetch CSV data
  const loadCsvData = useCallback(async () => {
    // Check if we have required data for fetching
    const canFetchByUrl = csvUrl && fetchCsvData;
    const canFetchByStreamId = getByStreamIdOnly && streamDetails?.streamId && fetchCsvData;

    if (!canFetchByUrl && !canFetchByStreamId) {
      setLoader(false);
      setError("Unable to load CSV: Missing required parameters");
      return;
    }

    try {
      setLoader(true);
      setError(null);

      const { value } = await fetchCsvData({
        url: getByStreamIdOnly ? undefined : csvUrl,
        clip: clipDetails || {},
        streamId: getByStreamIdOnly ? streamDetails?.streamId : undefined,
      });

      if (value?.status && value?.data) {
        parseCsvData(value.data);
      } else {
        setError("Failed to load CSV data");
        setLoader(false);
      }
    } catch (err) {
      const errorMessage = `Failed to fetch CSV: ${err.message}`;
      notification.error({ message: errorMessage });
      setError(errorMessage);
      setLoader(false);
    }
  }, [csvUrl, clipDetails, streamDetails?.streamId, fetchCsvData, parseCsvData, getByStreamIdOnly]);

  // Load data when modal opens or dependencies change
  useEffect(() => {
    if (isShowCsv) {
      loadCsvData();
    }
  }, [isShowCsv, loadCsvData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isShowCsv) {
      setColumns([]);
      setData([]);
      setError(null);
      setLoader(true);
    }
  }, [isShowCsv]);

  return (
    <Modal
      isOpen={isShowCsv}
      ariaHideApp={false}
      onRequestClose={handleClose}
      contentLabel="CSV Data Modal"
      portalClassName="react-modal"
      overlayClassName="modal"
      shouldCloseOnOverlayClick={false}
      className="modal-dialog react-modal-dialog-md modal-dialog-centered"
    >
      <div className="modal-content">
        <div className="react-modal-header">
          <h5 className="react-modal-title">Export CSV</h5>
        </div>
        <div className="react-modal-body">
          {loader ? (
            <div className="bg-loader-fade">
              <Spin className="fade-loader" indicator={antIcon} />
              <div className="loader-back-text">
                <h3>Loading CSV Data...</h3>
              </div>
            </div>
          ) : error ? (
            <div className="bg-loader-fade">
              <div className="loader-back-text">
                <h3>{error}</h3>
              </div>
            </div>
          ) : data.length > 0 ? (
            <div className="content-control-block long styled-scroll">
              <DataTable columns={columns} data={data} pagination highlightOnHover />
            </div>
          ) : (
            <div className="bg-loader-fade">
              <div className="loader-back-text">
                <h3>No Data Available</h3>
              </div>
            </div>
          )}
        </div>
        <div className="react-modal-footer text-center">
          <div className="react-foo-container">
            <button type="button" onClick={handleClose} className="btn btn-primary btn-white">
              Close
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShowCSV;
